import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Paper,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  MoreVert,
  Block,
  Report,
  AccessTime,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Message, Chat } from '../types';

const ChatPage: React.FC = () => {
  const { cardId } = useParams<{ cardId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [reportDialog, setReportDialog] = useState(false);
  const [blockDialog, setBlockDialog] = useState(false);
  const [cardExpired, setCardExpired] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [chatPartner] = useState({
    id: 'user2',
    name: '박영수',
    age: 58,
    location: '서울시 서초구',
    profileImage: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // 카드 만료 체크 (실제로는 API에서 확인)
    const checkCardExpiry = () => {
      // 더미 데이터로 2일 후 만료되는 카드
      const expiryDate = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000);
      const now = new Date();
      setCardExpired(now > expiryDate);
    };

    // 더미 메시지 데이터 로드
    const mockMessages: Message[] = [
      {
        id: '1',
        chatId: cardId || '',
        senderId: 'user2',
        content: '안녕하세요! 프로필을 보니 등산을 좋아하시는군요.',
        timestamp: new Date('2024-01-06T10:00:00'),
      },
      {
        id: '2',
        chatId: cardId || '',
        senderId: user.id,
        content: '네, 안녕하세요! 등산을 정말 좋아합니다. 주로 어떤 산을 오르시나요?',
        timestamp: new Date('2024-01-06T10:05:00'),
      },
      {
        id: '3',
        chatId: cardId || '',
        senderId: 'user2',
        content: '주로 관악산이나 북한산을 자주 갑니다. 혹시 같이 등산하실 의향이 있으신가요?',
        timestamp: new Date('2024-01-06T10:10:00'),
      },
    ];

    setMessages(mockMessages);
    checkCardExpiry();
  }, [user, navigate, cardId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (newMessage.trim() && !cardExpired && user) {
      const message: Message = {
        id: Date.now().toString(),
        chatId: cardId || '',
        senderId: user.id,
        content: newMessage.trim(),
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, message]);
      setNewMessage('');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleReport = () => {
    setReportDialog(true);
    handleMenuClose();
  };

  const handleBlock = () => {
    setBlockDialog(true);
    handleMenuClose();
  };

  const confirmReport = () => {
    // 실제로는 API 호출하여 신고 처리
    setReportDialog(false);
    alert('신고가 접수되었습니다. 검토 후 조치하겠습니다.');
  };

  const confirmBlock = () => {
    // 실제로는 API 호출하여 차단 처리
    setBlockDialog(false);
    alert('사용자를 차단했습니다.');
    navigate('/dashboard');
  };

  const formatTime = (timestamp: Date) => {
    return timestamp.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!user) return null;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Avatar sx={{ mr: 2 }} src={chatPartner.profileImage}>
              {chatPartner.name[0]}
            </Avatar>
            <Box>
              <Typography variant="h6" component="div">
                {chatPartner.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {chatPartner.age}세 • {chatPartner.location}
              </Typography>
            </Box>
          </Box>

          <IconButton color="inherit" onClick={handleMenuOpen}>
            <MoreVert />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleReport}>
          <Report sx={{ mr: 1 }} />
          신고하기
        </MenuItem>
        <MenuItem onClick={handleBlock}>
          <Block sx={{ mr: 1 }} />
          차단하기
        </MenuItem>
      </Menu>

      <Container 
        maxWidth="md" 
        sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          py: 2,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {cardExpired && (
          <Alert 
            severity="warning" 
            icon={<AccessTime />}
            sx={{ mb: 2 }}
          >
            큐피트 카드가 만료되었습니다. 더 이상 메시지를 보낼 수 없습니다.
          </Alert>
        )}

        <Box sx={{ flexGrow: 1 }}>
          {messages.map((message, index) => {
            const isMyMessage = message.senderId === user.id;
            const showTimestamp = index === messages.length - 1 || 
              messages[index + 1]?.senderId !== message.senderId ||
              new Date(messages[index + 1]?.timestamp).getTime() - new Date(message.timestamp).getTime() > 60000;

            return (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: isMyMessage ? 'flex-end' : 'flex-start',
                  mb: showTimestamp ? 2 : 0.5,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '70%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isMyMessage ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      bgcolor: isMyMessage ? 'primary.main' : 'grey.100',
                      color: isMyMessage ? 'white' : 'text.primary',
                      borderRadius: 2,
                      mb: showTimestamp ? 0.5 : 0,
                    }}
                  >
                    <Typography variant="body1">
                      {message.content}
                    </Typography>
                  </Paper>
                  {showTimestamp && (
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(message.timestamp)}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
          <div ref={messagesEndRef} />
        </Box>
      </Container>

      <Paper
        elevation={3}
        sx={{ p: 2, borderRadius: 0 }}
      >
        <Box component="form" onSubmit={handleSendMessage}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder={cardExpired ? "카드가 만료되어 메시지를 보낼 수 없습니다" : "메시지를 입력하세요..."}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              disabled={cardExpired}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                },
              }}
            />
            <IconButton
              type="submit"
              color="primary"
              disabled={!newMessage.trim() || cardExpired}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&:disabled': {
                  bgcolor: 'grey.300',
                },
              }}
            >
              <Send />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* 신고 다이얼로그 */}
      <Dialog open={reportDialog} onClose={() => setReportDialog(false)}>
        <DialogTitle>사용자 신고</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {chatPartner.name}님을 신고하시겠습니까?<br />
            신고 내용은 관리자가 검토 후 적절한 조치를 취합니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialog(false)}>취소</Button>
          <Button onClick={confirmReport} color="error">신고</Button>
        </DialogActions>
      </Dialog>

      {/* 차단 다이얼로그 */}
      <Dialog open={blockDialog} onClose={() => setBlockDialog(false)}>
        <DialogTitle>사용자 차단</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {chatPartner.name}님을 차단하시겠습니까?<br />
            차단하면 더 이상 이 사용자와 대화할 수 없습니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialog(false)}>취소</Button>
          <Button onClick={confirmBlock} color="error">차단</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatPage;